import {
  Box,
  Center,
  forwardRef,
  HStack,
  keyframes,
  Spinner,
  Stack,
  type StackProps,
  Text,
  useToken,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { CapacityAllocationTypeEnum } from "@src/__generated__/graphql";
import type {
  PlanningDailyViewItemModel,
  PlanningDailyViewUserByDayModel,
} from "@src/components/modules/resource-planning/timeline/models";
import { useResourcePlanningDailyViewStore } from "@src/components/modules/resource-planning/timeline/ResourcePlanningDayPage";
import { LOW_CONTRAST_RATIO } from "@src/components/ui-kit";
import { Icon, TrackedTimeIcon } from "@src/components/ui-kit/Icon";
import {
  ALLOCATION_ITEM_MIN_HEIGHT,
  ONE_HOUR_HEIGHT_OF_ALLOCATION,
} from "@src/constants/planning";
import {
  AllocationItemStyleEnum,
  DEFAULT_PLANNING_SETTING,
  PLANNING_SETTING_PROPERTIES,
} from "@src/stores/models/WorkspaceSettings";
import { calculateBackgroundColor } from "@src/utils/colors";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { formatDuration } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { secondsToHours } from "@src/utils/time";
import type { Time } from "@src/utils/types";
import { AllocationItemActionsOverlay } from "@src/widgets/AllocationItem/AllocationItemActionsOverlay";
import Color from "color";
import { format } from "date-fns";
import { compact } from "lodash";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { type ReactNode, useMemo } from "react";

export interface AllocationItemProps extends StackProps {
  userItem: PlanningDailyViewItemModel;
  day: PlanningDailyViewUserByDayModel["items"][0];
  calculatedTotalTime?: number;
  calculatedToTime?: Time;
  isResizing?: boolean;
  isLoading?: boolean;
  onAccept?: (userItem: PlanningDailyViewItemModel) => void;
  onAcceptForUser?: (userItem: PlanningDailyViewItemModel) => void;
  onDecline?: (userItem: PlanningDailyViewItemModel) => void;
  onDeclineForUser?: (userItem: PlanningDailyViewItemModel) => void;
}

export function calculateHeight(seconds: number): number {
  return secondsToHours(seconds) * ONE_HOUR_HEIGHT_OF_ALLOCATION;
}

type CompletionState =
  | "inProgress"
  | "inProgressOver"
  | "completedInTime"
  | "completedLate";

const getCompletionStateFromDuration = ({
  planned,
  tracked,
  completed,
}: {
  planned: number;
  tracked: number;
  completed?: boolean;
}): CompletionState => {
  if (!completed) return planned < tracked ? "inProgressOver" : "inProgress";
  if (planned < tracked) return "completedLate";
  return "completedInTime";
};

const getCompletionStateColor = (state: CompletionState): string => {
  const colors = {
    inProgress: "grey.800",
    inProgressOver: "red.500",
    completedInTime: "green.700",
    completedLate: "red.500",
  };
  return colors[state];
};

const blinking = keyframes({
  "50%": {
    opacity: 0.5,
  },
});

export const AllocationItem = observer(
  forwardRef<AllocationItemProps, "div">(
    (
      {
        userItem: allocationItem,
        calculatedTotalTime,
        calculatedToTime,
        day,
        isResizing,
        isLoading,
        onAccept,
        onAcceptForUser,
        onDecline,
        onDeclineForUser,
        ...props
      },
      ref,
    ) => {
      const {
        tracked_for_item,
        has_running_timer,
        completed,
        stats,
        isRepeating,
        createdBy,
        type,
        note,
        from_time,
      } = allocationItem;
      const total_time = calculatedTotalTime ?? allocationItem.total_time;
      const to_time = calculatedToTime ?? allocationItem.to_time;
      const { workspaceStore, timeTrackingStore } = useStore();
      const viewSettings =
        workspaceStore.settings?.users_planning_view_settings;
      const allocationItemStyle =
        workspaceStore.settings?.default_planning_view_settings
          .allocation_item_style;
      const resourcePlanningStore = useResourcePlanningDailyViewStore();
      const [grey500, teal500, grey50, grey300] = useToken("colors", [
        "grey.500",
        "teal.500",
        "grey.50",
        "grey.300",
      ]);
      const userColorLight = useMemo(() => {
        return calculateBackgroundColor(
          createdBy.profile?.hex_color ?? teal500,
        );
      }, [createdBy.profile?.hex_color]);

      const noteBg = useMemo(() => {
        return type === CapacityAllocationTypeEnum.TimeOff
          ? "#fff"
          : Color.hsl(Color(createdBy.profile?.hex_color).hue(), 100, 88).hex();
      }, [createdBy.profile?.hex_color]);

      const showAllocationOverlay = useMemo(() => {
        return (
          (type === CapacityAllocationTypeEnum.Task ||
            type === CapacityAllocationTypeEnum.Meeting ||
            type === CapacityAllocationTypeEnum.Imported) &&
          // Hide actions overlay while dragging for better performance
          !resourcePlanningStore.active
        );
      }, [resourcePlanningStore.active, type]);

      const { bg, accessibleTextColor } = useMemo<{
        bg: string;
        accessibleTextColor: string | undefined;
      }>(() => {
        let textColor: string | undefined;
        let userColorBg = userColorLight;
        switch (allocationItemStyle) {
          case AllocationItemStyleEnum.COLOR_SUBTLE:
            userColorBg = completed ? grey50 : userColorLight;
            break;
          case AllocationItemStyleEnum.COLOR_FILL:
            userColorBg = completed
              ? grey50
              : (createdBy.profile?.hex_color ?? teal500);
            break;
          case AllocationItemStyleEnum.NO_COLOR:
            userColorBg = "white";
            break;
        }
        const color =
          type === CapacityAllocationTypeEnum.TimeOff
            ? `repeating-linear-gradient(-45deg, ${grey500}, ${grey500} 4px, ${Color(
                grey500,
              ).lighten(0.1)} 4px, ${Color(grey500).lighten(0.1)} 8px )`
            : type === CapacityAllocationTypeEnum.Meeting
              ? `repeating-linear-gradient(-45deg, white, white 4px, ${userColorBg} 4px, ${userColorLight} 8px )`
              : userColorBg;

        if (
          type !== CapacityAllocationTypeEnum.TimeOff &&
          type !== CapacityAllocationTypeEnum.Meeting
        ) {
          textColor =
            Color(userColorBg).contrast(Color("white")) > LOW_CONTRAST_RATIO
              ? "#fff"
              : completed
                ? grey300
                : undefined;
        }

        return { bg: color, accessibleTextColor: textColor };
      }, [
        workspaceStore.settings?.default_planning_view_settings,
        createdBy,
        completed,
      ]);

      const completionState = getCompletionStateFromDuration({
        planned: total_time ?? 0,
        tracked: tracked_for_item ? tracked_for_item : 0,
        completed: completed,
      });

      const trackedTimeColor = getCompletionStateColor(completionState);

      const name = useMemo(() => {
        switch (type) {
          case CapacityAllocationTypeEnum.Task:
          case CapacityAllocationTypeEnum.Meeting:
            return stats?.task_name;
          case CapacityAllocationTypeEnum.TimeOff:
            return stats?.time_off_type_name;
          case CapacityAllocationTypeEnum.Imported:
            return stats?.title;
          default:
            return undefined;
        }
      }, [viewSettings?.show_task]);

      const text_order = useMemo(() => {
        if (
          viewSettings?.text_order &&
          viewSettings.text_order.toString() !==
            DEFAULT_PLANNING_SETTING.text_order.toString()
        ) {
          return toJS(viewSettings.text_order);
        }

        return (
          toJS(
            workspaceStore.settings?.default_planning_view_settings.text_order,
          ) ?? DEFAULT_PLANNING_SETTING.text_order
        );
      }, [
        viewSettings?.text_order,
        workspaceStore.settings?.default_planning_view_settings,
      ]);

      const texts = useMemo<ReactNode[]>(() => {
        if (type === CapacityAllocationTypeEnum.Imported) {
          return compact([stats?.title, t`Imported from External Calendar`]);
        }

        const orderedTexts = compact([
          viewSettings?.show_project &&
            stats?.task_id && {
              text: stats.project_id
                ? `${stats?.project_code} - ${stats?.project_title}`
                : t`Unassigned`,
              id: PLANNING_SETTING_PROPERTIES.SHOW_PROJECT as const,
            },
          viewSettings?.show_title &&
            stats?.title && {
              text: stats.title,
              id: PLANNING_SETTING_PROPERTIES.SHOW_TITLE as const,
            },
          ...(viewSettings?.show_project
            ? [
                (viewSettings?.show_task || viewSettings?.show_budget_item) &&
                  (name || stats?.our_work_budget_item_title) && {
                    text: `${viewSettings?.show_task && name ? name : ""}${
                      viewSettings?.show_budget_item &&
                      stats?.our_work_budget_item_title
                        ? viewSettings?.show_task
                          ? ` (${stats?.our_work_budget_item_title})`
                          : stats?.our_work_budget_item_title
                        : ""
                    }`,
                    id: PLANNING_SETTING_PROPERTIES.SHOW_TASK as const,
                  },
              ]
            : [
                viewSettings?.show_task &&
                  name && {
                    text: name,
                    id: PLANNING_SETTING_PROPERTIES.SHOW_TASK as const,
                  },
                viewSettings?.show_budget_item &&
                  stats?.our_work_budget_item_title && {
                    text: stats?.our_work_budget_item_title,
                    id: PLANNING_SETTING_PROPERTIES.SHOW_BUDGET_ITEM as const,
                  },
              ]),
        ]).sort((a, b) => {
          if (!a || !b) return 0;
          return text_order.indexOf(a.id) - text_order.indexOf(b.id);
        });

        return compact([
          ...orderedTexts.map(({ text }) => text),
          viewSettings?.show_note && !!note && (
            <HStack
              h="24px"
              mt="6px"
              px="7px"
              py="5px"
              bg={noteBg}
              rounded="md"
            >
              <Icon
                name="message-text-square-01"
                color={
                  type === CapacityAllocationTypeEnum.TimeOff
                    ? "black"
                    : accessibleTextColor
                }
                w="14px"
                h="14px"
              />
              <Text
                color={
                  type === CapacityAllocationTypeEnum.TimeOff
                    ? "black"
                    : accessibleTextColor
                }
                fontSize="12px"
                fontWeight="normal"
                lineHeight="14.4px"
                letterSpacing="0.3px"
                textDecoration={completed ? "line-through" : "initial"}
                wordBreak="break-word"
                noOfLines={1}
              >
                {note}
              </Text>
            </HStack>
          ),
        ]);
      }, [
        name,
        stats?.our_work_budget_item_title,
        viewSettings,
        note,
        text_order,
        completed,
        stats?.project_title,
        stats?.project_code,
      ]);

      const before = useMemo(() => {
        return type === CapacityAllocationTypeEnum.Task ||
          type === CapacityAllocationTypeEnum.Meeting
          ? {
              position: "absolute",
              content: '""',
              w: "4px",
              h: "full",
              left: 0,
              top: 0,
              bg: createdBy.profile?.hex_color,
              borderLeftRadius: "md",
            }
          : undefined;
      }, [type]);

      const deadlineText = useMemo(() => {
        return stats?.deadline_time && stats?.deadline === day.date
          ? stats.deadline_time.slice(0, 5)
          : stats?.deadline && format(stats?.deadline, "d. M.");
      }, [stats?.deadline_time, stats?.deadline]);

      return (
        <Stack
          ref={ref}
          // Static position allows `HStack` holding allocation duration to be always
          // visible when isResizing, because next relative parent is `Resizable`.
          pos={isResizing ? "static" : "relative"}
          align="stretch"
          w="full"
          h="full"
          minH={ALLOCATION_ITEM_MIN_HEIGHT}
          p="1"
          pt="2"
          pl="3"
          bg={bg}
          shadow="base"
          _before={before}
          animation={
            has_running_timer ? `${blinking} 1s infinite linear` : undefined
          }
          role="group"
          rounded="md"
          spacing="0"
          {...props}
        >
          {showAllocationOverlay && (
            <AllocationItemActionsOverlay
              userItem={allocationItem}
              display="none"
              _groupHover={{
                display: "flex",
              }}
            />
          )}

          {resourcePlanningStore.syncingIds.includes(allocationItem.id) && (
            <Center
              pos="absolute"
              zIndex={10}
              top="0"
              right="0"
              bottom="0"
              left="0"
              bg="rgba(255, 255, 255, 0.7)"
            >
              <Spinner color="teal.500" size="md" />
            </Center>
          )}

          <Box>
            {texts.map((text, index) => (
              <WrapComponent
                key={`allocation-item-text-${text}`}
                if={isRepeating && index === 0}
                with={(children) => (
                  <HStack>
                    <Icon name="hierarchy" />
                    {children}
                  </HStack>
                )}
              >
                <Text
                  sx={
                    index === 0
                      ? {
                          color: accessibleTextColor,
                          fontSize: "13px",
                          fontWeight: "medium",
                          lineHeight: "15.08px",
                          letterSpacing: "0.3px",
                          wordBreak: "break-word",
                          textDecoration: completed
                            ? "line-through"
                            : "initial",
                        }
                      : index === 1
                        ? {
                            w: "full",
                            color:
                              type === CapacityAllocationTypeEnum.TimeOff
                                ? "white"
                                : allocationItemStyle ===
                                    AllocationItemStyleEnum.COLOR_FILL
                                  ? accessibleTextColor
                                  : "grey.500",
                            fontSize: "11px",
                            fontWeight: "normal",
                            lineHeight: "16px",
                            letterSpacing: "0.3px",
                            textDecoration: completed
                              ? "line-through"
                              : "initial",
                            wordBreak: "break-word",
                          }
                        : undefined
                  }
                  noOfLines={1}
                >
                  {text}
                </Text>
              </WrapComponent>
            ))}
          </Box>

          <HStack
            pos="absolute"
            zIndex="1"
            right="1"
            bottom="1"
            left="1"
            justify="end"
          >
            {stats?.deadline && !completed && (
              <Center h="4" px="1" bg="red.100" rounded="base">
                <Text color="red.700" fontSize="2xs" fontWeight="semibold">
                  {deadlineText}
                </Text>
              </Center>
            )}

            <HStack
              h="5"
              px="1"
              bg={has_running_timer ? "green.100" : "whiteAlpha.600"}
              divider={
                <Text color="grey.600" fontSize="2xs">
                  &nbsp;/&nbsp;
                </Text>
              }
              rounded="base"
            >
              {(tracked_for_item || has_running_timer) && (
                <HStack>
                  <TrackedTimeIcon
                    w="3"
                    h="3"
                    color={has_running_timer ? "green.700" : trackedTimeColor}
                  />
                  <Text
                    color={has_running_timer ? "green.700" : trackedTimeColor}
                    fontSize="2xs"
                    fontWeight="medium"
                  >
                    {has_running_timer
                      ? formatDuration(
                          (tracked_for_item ?? 0) +
                            (timeTrackingStore.runningEntry
                              ?.tracked_time_with_running_timer ?? 0),
                          { noPadStart: true },
                        )
                      : formatDuration(tracked_for_item ?? 0, {
                          noPadStart: true,
                        })}
                  </Text>
                </HStack>
              )}
              <Text fontSize="2xs" fontWeight="medium">
                {formatDuration(total_time ?? 0, {
                  noPadStart: true,
                  roundSeconds: true,
                })}
              </Text>
            </HStack>
            {from_time && to_time && (
              <HStack
                h="5"
                px="1"
                bg={has_running_timer ? "green.100" : "whiteAlpha.600"}
                rounded="base"
              >
                <Text fontSize="2xs" fontWeight="medium">
                  {from_time}&nbsp;-&nbsp;{to_time}
                </Text>
              </HStack>
            )}
          </HStack>
        </Stack>
      );
    },
  ),
);
